import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  Button,
  Input,
  Checkbox,
  Card,
  CardHeader,
  Badge,
  Dropdown,
  Option,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  getAdminPermissions,
  getAdminUsers,
  setUserPermissions,
  type PermissionInfo,
  type UserPermissionSummary,
} from '../../api/adminClient';
import { useAuthArgs } from '../../hooks/useAuthArgs';

const useStyles = makeStyles({
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
    alignItems: 'start',
  },
  card: { padding: tokens.spacingVerticalM },
  permList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, marginTop: tokens.spacingVerticalM },
  editorRow: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'end', flexWrap: 'wrap' },
  checks: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, marginTop: tokens.spacingVerticalM },
  catalogItem: { display: 'flex', flexDirection: 'column', gap: '2px', paddingBottom: tokens.spacingVerticalS },
});

/**
 * Admin > Permissions. Shows the permission catalog and an editor that replaces a user's granted
 * permission set. Choosing a known user pre-fills their current grants; a free-text email lets an
 * admin grant permissions to a not-yet-seen user.
 */
export function PermissionsPage() {
  const styles = useStyles();

  const [permissions, setPermissions] = useState<PermissionInfo[]>([]);
  const [users, setUsers] = useState<UserPermissionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [email, setEmail] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  const getAuthArgs = useAuthArgs();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to manage permissions.');
        return;
      }
      const [perms, userList] = await Promise.all([getAdminPermissions(auth), getAdminUsers(auth)]);
      setPermissions(perms);
      setUsers(userList);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [getAuthArgs]);

  useEffect(() => {
    void load();
  }, [load]);

  const usersByEmail = useMemo(() => new Map(users.map((u) => [u.email, u])), [users]);

  const pickUser = useCallback(
    (value: string) => {
      setEmail(value);
      const existing = usersByEmail.get(value.trim().toLowerCase());
      setSelected(new Set(existing?.permissions ?? []));
      setNotice(null);
    },
    [usersByEmail],
  );

  const toggle = (name: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(name);
      else next.delete(name);
      return next;
    });
  };

  const save = useCallback(async () => {
    const target = email.trim().toLowerCase();
    if (!target) {
      setError('Enter a user email to update.');
      return;
    }
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to save permissions.');
        return;
      }
      const result = await setUserPermissions(auth, { email: target, permissions: [...selected] });
      setNotice(`Saved ${result.length} permission(s) for ${target}.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  }, [email, selected, load, getAuthArgs]);

  if (loading) {
    return <Spinner label="Loading permissions…" />;
  }

  return (
    <section>
      <Text as="h1" size={600} weight="semibold" style={{ display: 'block', marginBottom: 16 }}>
        Permissions
      </Text>

      {error && (
        <MessageBar intent="error" style={{ marginBottom: 16 }}>
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
      {notice && (
        <MessageBar intent="success" style={{ marginBottom: 16 }}>
          <MessageBarBody>{notice}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.grid}>
        <Card className={styles.card}>
          <CardHeader header={<Text weight="semibold">Permission catalog</Text>} />
          <div className={styles.permList}>
            {permissions.length === 0 ? (
              <Text italic>No permissions defined.</Text>
            ) : (
              permissions.map((p) => (
                <div key={p.name} className={styles.catalogItem}>
                  <Badge appearance="tint" color="brand" style={{ alignSelf: 'flex-start' }}>{p.name}</Badge>
                  {p.description && <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>{p.description}</Text>}
                </div>
              ))
            )}
          </div>
        </Card>

        <Card className={styles.card}>
          <CardHeader header={<Text weight="semibold">Edit a user's permissions</Text>} />
          <div className={styles.editorRow} style={{ marginTop: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 260 }}>
              <Text size={200}>Existing user</Text>
              <Dropdown
                placeholder="Select a user"
                selectedOptions={usersByEmail.has(email.trim().toLowerCase()) ? [email.trim().toLowerCase()] : []}
                onOptionSelect={(_, data) => data.optionValue && pickUser(data.optionValue)}
              >
                {users.map((u) => (
                  <Option key={u.email} value={u.email}>{u.email}</Option>
                ))}
              </Dropdown>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 260 }}>
              <Text size={200}>Or enter an email</Text>
              <Input
                value={email}
                placeholder="user@microsoft.com"
                onChange={(_, data) => pickUser(data.value)}
              />
            </div>
          </div>

          <div className={styles.checks}>
            {permissions.map((p) => (
              <Checkbox
                key={p.name}
                checked={selected.has(p.name)}
                onChange={(_, data) => toggle(p.name, data.checked === true)}
                label={p.name}
              />
            ))}
          </div>

          <div style={{ marginTop: 16 }}>
            <Button appearance="primary" onClick={() => void save()} disabled={saving || !email.trim()}>
              {saving ? 'Saving…' : 'Save permissions'}
            </Button>
          </div>
        </Card>
      </div>
    </section>
  );
}
